import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-bissa-blue hover:text-bissa-gold transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-4xl font-bold text-bissa-blue mb-2">Terms of Service</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using Bissa.ai ("the Service"), you accept and agree to be bound by the terms 
            and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
          </p>

          <h2>2. Description of Service</h2>
          <p>
            Bissa.ai provides a customer relationship management (CRM) platform that helps businesses manage 
            contacts, companies, deals, and sales processes. The service includes features such as contact management, 
            sales pipeline tracking, task management, and analytics.
          </p>

          <h2>3. User Accounts</h2>
          <p>
            To access certain features of the Service, you must register for an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain the security of your password and identification</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
            <li>Accept responsibility for all activities under your account</li>
          </ul>

          <h2>4. Subscription Plans and Billing</h2>
          <p>
            Bissa.ai offers various subscription plans including Free, Growth ($15/month), Pro ($35/month), 
            and Business ($79/month). By subscribing to a paid plan, you agree to:
          </p>
          <ul>
            <li>Pay all applicable fees for your chosen subscription plan</li>
            <li>Automatic renewal unless cancelled before the renewal date</li>
            <li>Usage limits as specified in your chosen plan</li>
            <li>Potential service suspension if payment fails</li>
          </ul>

          <h2>5. Acceptable Use</h2>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Violate any laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Transmit harmful, offensive, or inappropriate content</li>
            <li>Attempt to gain unauthorized access to the system</li>
            <li>Use the service for spam or unsolicited communications</li>
            <li>Reverse engineer or attempt to extract source code</li>
          </ul>

          <h2>6. Data Ownership and Security</h2>
          <p>
            You retain ownership of all data you submit to Bissa.ai. We implement industry-standard security 
            measures to protect your data, including encryption and secure data centers. However, no method 
            of transmission over the internet is 100% secure.
          </p>

          <h2>7. Service Availability</h2>
          <p>
            While we strive for high availability, we do not guarantee uninterrupted service. We may 
            temporarily suspend service for maintenance, updates, or unforeseen circumstances. We are 
            not liable for any loss or damage resulting from service interruptions.
          </p>

          <h2>8. Intellectual Property</h2>
          <p>
            The Service and its original content, features, and functionality are owned by Bissa.ai and 
            are protected by international copyright, trademark, patent, trade secret, and other intellectual 
            property laws.
          </p>

          <h2>9. Termination</h2>
          <p>
            Either party may terminate your account at any time. Upon termination:
          </p>
          <ul>
            <li>Your access to the Service will be immediately suspended</li>
            <li>You may export your data for 30 days after termination</li>
            <li>We may delete your data after the 30-day period</li>
            <li>Paid subscription fees are non-refundable except as stated in our Refund Policy</li>
          </ul>

          <h2>10. Limitation of Liability</h2>
          <p>
            In no event shall Bissa.ai be liable for any indirect, incidental, special, consequential, 
            or punitive damages, including without limitation, loss of profits, data, use, goodwill, 
            or other intangible losses.
          </p>

          <h2>11. Disclaimer</h2>
          <p>
            The Service is provided on an "as is" and "as available" basis. Bissa.ai makes no representations 
            or warranties of any kind, express or implied, as to the operation of the Service or the information 
            included on the Service.
          </p>

          <h2>12. Governing Law</h2>
          <p>
            These Terms shall be interpreted and governed by the laws of the jurisdiction where Bissa.ai 
            operates, without regard to conflict of law provisions.
          </p>

          <h2>13. Changes to Terms</h2>
          <p>
            We reserve the right to modify these terms at any time. We will notify users of significant 
            changes via email or through the Service. Continued use after changes constitutes acceptance 
            of new terms.
          </p>

          <h2>14. Contact Information</h2>
          <p>
            If you have questions about these Terms of Service, please contact us:
          </p>
          <ul>
            <li>Through the support system in your Bissa.ai dashboard</li>
            <li>By email at <a href="mailto:leslie@iabadvisors.com" className="text-bissa-blue hover:underline">leslie@iabadvisors.com</a></li>
            <li>Through our contact form on the website</li>
          </ul>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="font-semibold mb-2">Bissa.ai</p>
            <p>
              Email: <a href="mailto:leslie@iabadvisors.com" className="text-bissa-blue hover:underline">leslie@iabadvisors.com</a><br />
              Website: <a href="https://bissa.ai" className="text-bissa-blue hover:underline">https://bissa.ai</a>
            </p>
          </div>

          <div className="mt-12 p-6 bg-bissa-blue-50 rounded-lg border border-bissa-blue-200">
            <h3 className="text-bissa-blue font-semibold mb-2">Questions about our Terms?</h3>
            <p className="text-bissa-blue-700">
              Contact our legal team through the support system in your Bissa.ai dashboard or reach 
              out via our contact form for clarification on any terms or conditions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;